import VariantPicker from '@theme/variant-picker';
import { Component } from '@theme/component';
import { debounce, isDesktopBreakpoint, mediaQueryLarge } from '@theme/utilities';
import { ThemeEvents, VariantSelectedEvent, VariantUpdateEvent, SlideshowSelectEvent } from '@theme/events';
import { morph } from '@theme/morph';

/**
 * A custom element that displays a product card.
 *
 * @typedef {object} Refs
 * @property {HTMLAnchorElement} productCardLink - The product card link element.
 * @property {import('slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {import('quick-add').QuickAddComponent} [quickAdd] - The quick add component.
 * @property {HTMLElement} [cardGallery] - The card gallery component.
 *
 * @extends {Component<Refs>}
 */
export class ProductCard extends Component {
  requiredRefs = ['productCardLink'];

  get productPageUrl() {
    return this.refs.productCardLink.href;
  }

  #fetchProductPageHandler = () => {
    if (!this.refs.quickAdd?.cachedProductHtml) {
      this.refs.quickAdd?.fetchProductPage(this.productPageUrl);
    }
  };

  /**
   * Navigates to a URL link. Respects modifier keys for opening in new tab/window.
   * @param {Event} event - The event that triggered the navigation.
   * @param {URL} url - The URL to navigate to.
   */
  #navigateToURL = (event, url) => {
    // Check for modifier keys that should open in new tab/window (only for mouse events)
    const shouldOpenInNewTab =
      event instanceof MouseEvent && (event.metaKey || event.ctrlKey || event.shiftKey || event.button === 1);

    if (shouldOpenInNewTab) {
      event.preventDefault();
      window.open(url.href, '_blank');
      return;
    } else {
      window.location.href = url.href;
    }
  };

  connectedCallback() {
    super.connectedCallback();
  
    const link = this.refs.productCardLink;
    if (!(link instanceof HTMLAnchorElement)) throw new Error('Product card link not found');
    this.#handleQuickAdd();
  
    this.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);
    this.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected);
    mediaQueryLarge.addEventListener('change', this.#handleQuickAdd);
  
    this.addEventListener('click', this.navigateToProduct);
    this.addEventListener(SlideshowSelectEvent.eventName, this.#handleSlideshowSelect);
    queueMicrotask(() => {
      this.#bindGalleryIndicatorScroll();
      this.#updateGalleryIndicator();
    });

  
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', this.navigateToProduct);
    // mediaQueryLarge はグローバルに存在し続けるため、解除しないとカード再描画のたびに参照が残る
    mediaQueryLarge.removeEventListener('change', this.#handleQuickAdd);
    this.removeEventListener('pointerenter', this.#fetchProductPageHandler);
    this.removeEventListener('focusin', this.#fetchProductPageHandler);
    this.#galleryIndicatorScroller?.removeEventListener('scroll', this.#handleGalleryIndicatorScroll);
  }

  #preloadNextPreviewImage() {
    const currentSlide = this.refs.slideshow?.slides?.[this.refs.slideshow?.current];
    currentSlide?.nextElementSibling?.querySelector('img[loading="lazy"]')?.removeAttribute('loading');
  }

  /**
   * Handles the quick add event.
   */
  #handleQuickAdd = () => {
    this.removeEventListener('pointerenter', this.#fetchProductPageHandler);
    this.removeEventListener('focusin', this.#fetchProductPageHandler);

    if (isDesktopBreakpoint()) {
      this.addEventListener('pointerenter', this.#fetchProductPageHandler);
      this.addEventListener('focusin', this.#fetchProductPageHandler);
    }
  };

  /**
   * Handles the variant selected event.
   * @param {VariantSelectedEvent} event - The variant selected event.
   */
  #handleVariantSelected = (event) => {
    if (event.target !== this.variantPicker) {
      this.variantPicker?.updateSelectedOption(event.detail.resource.id);
    }
  };

  /**
   * Handles the variant update event.
   * Updates price, checks for unavailable variants, and updates product URL.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    // Stop the event from bubbling up to the section, variant updates triggered from product cards are fully handled
    // by this component and should not affect anything outside the card.
    event.stopPropagation();

    this.updatePrice(event);
    this.#isUnavailableVariantSelected(event);
    this.#updateProductUrl(event);
    this.refs.quickAdd?.fetchProductPage(this.productPageUrl);

    if (event.target !== this.variantPicker) {
      this.variantPicker?.updateVariantPicker(event.detail.data.html);
    }

    this.#updateVariantImages();
    this.#previousSlideIndex = null;
    this.#updateGalleryIndicator();
  };

  /**
   * Updates the DOM with a new price.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updatePrice(event) {
    const newHtml = event.detail.data.html;
    // カード内の価格表示は複数ある（通常の価格ブロックと、ズームアウトグリッド用の表示）。
    // DOM の並び順に依存せず、ブロックIDで対応付けられるものはIDで、それ以外は同じ並び順で更新する。
    const priceElements = this.querySelectorAll('product-price');
    const newPriceElements = newHtml.querySelectorAll('product-price');

    priceElements.forEach((priceElement, index) => {
      const priceContainer = priceElement.querySelector(`[ref='priceContainer']`);
      if (!priceContainer) return;

      const blockId = priceElement instanceof HTMLElement ? priceElement.dataset.blockId : undefined;
      const newPriceElement = blockId
        ? newHtml.querySelector(`product-price[data-block-id="${blockId}"]`)
        : newPriceElements[index];

      const newPriceContainer = newPriceElement?.querySelector(`[ref='priceContainer']`);
      if (newPriceContainer) morph(priceContainer, newPriceContainer);
    });
  }

  /**
   * Updates the product URL based on the variant update event.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #updateProductUrl(event) {
    const anchorElement = event.detail.data.html?.querySelector('product-card a');
    const featuredMediaUrl = event.detail.data.html
      ?.querySelector('product-card-link')
      ?.getAttribute('data-featured-media-url');

    // If the product card is inside a product link, update the product link's featured media URL
    if (featuredMediaUrl && this.closest('product-card-link'))
      this.closest('product-card-link')?.setAttribute('data-featured-media-url', featuredMediaUrl);

    if (anchorElement instanceof HTMLAnchorElement) {
      // If the href is empty, don't update the product URL eg: unavailable variant
      if (anchorElement.getAttribute('href')?.trim() === '') return;

      this.refs.productCardLink.href = anchorElement.href;
    }
  }

  /**
   * Checks if an unavailable variant is selected.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #isUnavailableVariantSelected(event) {
    const allVariants = /** @type {NodeListOf<HTMLInputElement>} */ (
      event.detail.data.html.querySelectorAll('input:checked')
    );

    for (const variant of allVariants) {
      this.#toggleAddToCartButton(variant.dataset.optionAvailable === 'true');
    }
  }

  /**
   * Toggles the add to cart button state.
   * @param {boolean} enable - Whether to enable or disable the button.
   */
  #toggleAddToCartButton(enable) {
    const addToCartButton = this.querySelector('.add-to-cart__button button');

    if (addToCartButton instanceof HTMLButtonElement) {
      addToCartButton.disabled = !enable;
    }
  }

  /**
   * Hide the variant images that are not for the selected variant.
   */
  #updateVariantImages() {
    const { slideshow } = this.refs;
    
    if (!this.variantPicker?.selectedOption) {
      // バリアント未選択時は、バリアント画像をすべて非表示にして通常の商品画像を表示
      if (slideshow) {
        const { slides = [] } = slideshow.refs;
        for (const slide of slides) {
          // variant-image属性を持つスライドだけ非表示
          if (slide.getAttribute('variant-image') != null) {
            slide.hidden = true;
          } else {
            slide.hidden = false;
          }
        }
        // 最初のスライドを表示
        slideshow.select(0, undefined, { animate: false });
      }
      return;
    }
  
    const selectedImageId = this.variantPicker?.selectedOption.dataset.optionMediaId;
  
    if (slideshow && selectedImageId) {
      const { slides = [] } = slideshow.refs;
  
      for (const slide of slides) {
        if (slide.getAttribute('variant-image') == null) continue;
  
        slide.hidden = slide.getAttribute('slide-id') !== selectedImageId;
        slide.setAttribute('aria-hidden', slide.hidden ? 'true' : 'false');
      }
      
      slideshow.select({ id: selectedImageId }, undefined, { animate: false });
    }
  }
  /**
   * Gets all variant inputs.
   * @returns {NodeListOf<HTMLInputElement>} All variant input elements.
   */
  get allVariants() {
    return this.querySelectorAll('input[data-variant-id]');
  }

  /**
   * Gets the variant picker component.
   * @returns {VariantPicker | null} The variant picker component.
   */
  get variantPicker() {
    return this.querySelector('swatches-variant-picker-component');
  }
  /** @type {number | null} */
  #previousSlideIndex = null;
  /** @type {HTMLElement | null} */
  #galleryIndicatorScroller = null;

  /**
   * Handles the slideshow select event.
   * @param {SlideshowSelectEvent} event - The slideshow select event.
   */
  #handleSlideshowSelect = (event) => {
    this.#updateGalleryIndicator(event.detail.index);

    if (event.detail.userInitiated) {
      this.#previousSlideIndex = event.detail.index;
    }
  };

  #handleGalleryIndicatorScroll = () => {
    this.#updateGalleryIndicator();
  };

  #bindGalleryIndicatorScroll() {
    const scroller = this.refs.slideshow?.refs?.scroller;
    if (!scroller || scroller === this.#galleryIndicatorScroller) return;

    this.#galleryIndicatorScroller?.removeEventListener('scroll', this.#handleGalleryIndicatorScroll);
    this.#galleryIndicatorScroller = scroller;
    this.#galleryIndicatorScroller.addEventListener('scroll', this.#handleGalleryIndicatorScroll, { passive: true });
  }

  /**
   * Moves the mobile gallery indicator to the currently selected slide.
   * @param {number} [index] - The selected slide index.
   */
  #updateGalleryIndicator(index = this.refs.slideshow?.current ?? 0) {
    const { cardGallery, slideshow } = this.refs;
    if (!cardGallery || !slideshow) return;

    const slideCount = slideshow.slides?.length ?? 0;

    if (slideCount <= 1) {
      cardGallery.style.setProperty('--card-gallery-slide-count', '1');
      cardGallery.style.setProperty('--card-gallery-indicator-progress', '0');
      return;
    }

    const currentIndex = Math.min(Math.max(index, 0), slideCount - 1);
    const scroller = slideshow.refs?.scroller;
    const maxScroll = scroller ? scroller.scrollWidth - scroller.clientWidth : 0;
    const scrollProgress = maxScroll > 0 && scroller ? scroller.scrollLeft / maxScroll : null;
    const indicatorProgress = scrollProgress === null ? currentIndex / (slideCount - 1) : scrollProgress;

    cardGallery.style.setProperty('--card-gallery-slide-count', `${slideCount}`);
    cardGallery.style.setProperty('--card-gallery-indicator-progress', `${indicatorProgress}`);
  }

  /**
   * Previews a variant.
   * @param {string} id - The id of the variant to preview.
   */
  // previewVariant(id) {
  //   const { slideshow } = this.refs;

  //   if (!slideshow) return;

  //   // this.resetVariant.cancel();
  //   slideshow.select({ id }, undefined, { animate: false });
  // }

  /**
   * Previews the next image.
   * @param {PointerEvent} event - The pointer event.
   */
  previewImage(event) {
    if (event.pointerType !== 'mouse') return;
  
    const { slideshow } = this.refs;
  
    if (!slideshow) return;
  
    // バリエーションがある商品の場合
    if (this.variantPicker) {
      const currentSlideIndex = slideshow.current;
      
      if (currentSlideIndex === 0) {
        // 1枚目表示中 → バリアント画像を表示
        const selectedImageId = this.variantPicker.selectedOption?.dataset.optionMediaId;
        if (selectedImageId) {
          slideshow.select({ id: selectedImageId }, undefined, { animate: false });
        } else {
          // バリアント未選択の場合は2枚目を表示
          slideshow.next(undefined, { animate: false });
        }
      } else {
        // バリアント画像表示中 → 1枚目を表示
        slideshow.select(0, undefined, { animate: false });
      }
      return;
    }
  
    // バリエーションがない商品の場合（従来の動作）
    if (this.#previousSlideIndex != null && this.#previousSlideIndex > 0) {
      slideshow.select(this.#previousSlideIndex, undefined, { animate: false });
    } else {
      slideshow.next(undefined, { animate: false });
    }
  }

  /**
   * Resets the image to the variant image.
   * @param {PointerEvent} event - The pointer event.
   */
  resetImage(event) {
    if (event.pointerType !== 'mouse') return;
  
    const { slideshow } = this.refs;
  
    if (!slideshow) return;
  
    // バリエーションがある商品の場合
    if (this.variantPicker) {
      const currentSlideIndex = slideshow.current;
      
      if (currentSlideIndex === 0) {
        // 1枚目表示中 → バリアント画像に戻す
        const selectedImageId = this.variantPicker.selectedOption?.dataset.optionMediaId;
        if (selectedImageId) {
          slideshow.select({ id: selectedImageId }, undefined, { animate: false });
        }
      } else {
        // バリアント画像表示中 → 1枚目に戻す
        slideshow.select(0, undefined, { animate: false });
      }
      return;
    }
  
    // バリエーションがない商品の場合（従来の動作）
    slideshow.select(0, undefined, { animate: false });
  }

  /**
   * Resets the image to the variant image.
   */
  // #resetVariant = () => {
  //   const { slideshow } = this.refs;

  //   if (!slideshow) return;

  //   const initialSlide = slideshow.initialSlide;
  //   const slideId = initialSlide?.getAttribute('slide-id');
  //   if (initialSlide && slideshow.slides?.includes(initialSlide) && slideId) {
  //     slideshow.select({ id: slideId }, undefined, { animate: false });
  //     return;
  //   } else if (!this.variantPicker?.selectedOption) {
  //     slideshow.previous(undefined, { animate: false });
  //     return;
  //   }

  //   const id = this.variantPicker.selectedOption.dataset.optionMediaId;
  //   if (!id) {
  //     slideshow.previous(undefined, { animate: false });
  //     return;
  //   }

  //   slideshow.select({ id }, undefined, { animate: false });
  // };

  /**
   * Intercepts the click event on the product card anchor, we want
   * to use this to add an intermediate state to the history.
   * This intermediate state captures the page we were on so that we
   * navigate back to the same page when the user navigates back.
   * In addition to that, it captures the product card anchor so that we
   * have the specific product card in view.
   *
   * A product card can have other interactive elements like variant picker,
   * so we do not navigate if the click was on one of those elements.
   *
   * @param {Event} event
   */
  navigateToProduct = (event) => {
    if (!(event.target instanceof Element)) return;

    // Don't navigate if this product card is marked as no-navigation (e.g., in theme editor)
    if (this.hasAttribute('data-no-navigation')) return;

    const interactiveElement = event.target.closest('button, input, label, select, a, [tabindex="1"]');

    // If the click was on an interactive element which is not the main link, do nothing.
    if (interactiveElement && interactiveElement !== this.refs.productCardLink) {
      return;
    }

    const link = this.refs.productCardLink;
    if (!link.href) return;
    const linkURL = new URL(link.href);

    const productCardAnchor = link.getAttribute('id');
    if (!productCardAnchor) return;

    const url = new URL(window.location.href);
    const parent = this.closest('li');
    url.hash = productCardAnchor;
    if (parent && parent.dataset.page) {
      url.searchParams.set('page', parent.dataset.page);
    }

    if (!window.Shopify.designMode) {
      history.replaceState({}, '', url.toString());
    }

    this.#navigateToURL(event, linkURL);
  };

  /**
   * Resets the variant.
   */
  // resetVariant = debounce(this.#resetVariant, 100);
}

if (!customElements.get('product-card')) {
  customElements.define('product-card', ProductCard);
}

/**
 * A custom element that displays a variant picker with swatches.
 *
 * @typedef {object} SwatchesRefs
 * @property {HTMLElement} overflowList
 *
 * @extends {VariantPicker<SwatchesRefs>}
 */
class SwatchesVariantPickerComponent extends VariantPicker {
  /**
   * Shows all swatches.
   * @param {Event} [event] - The event that triggered the show all swatches.
   */
  showAllSwatches(event) {
    event?.preventDefault();

    const { overflowList } = this.refs;

    if (overflowList instanceof OverflowList) {
      overflowList.showAll();
    }
  }
}

if (!customElements.get('swatches-variant-picker-component')) {
  customElements.define('swatches-variant-picker-component', SwatchesVariantPickerComponent);
}
